import { ToastProvider as KitToastProvider, useToast as useKitToast } from '@d-edge/overnight-hotelier-react'
import { createContext, useContext, useMemo, type ReactNode } from 'react'
import styles from './ui.module.css'

type ToastKind = 'info' | 'success' | 'error'

interface ToastApi {
  notify: (message: string, kind?: ToastKind) => void
  error: (error: unknown, prefix?: string) => void
}

const ToastContext = createContext<ToastApi | null>(null)

const variation = { info: 'information', success: 'success', error: 'error' } as const

export function ToastProvider({ children }: { children: ReactNode }) {
  return (
    <KitToastProvider labelA11y="Notifications" closeToastLabelA11y="Close">
      <Bridge>{children}</Bridge>
    </KitToastProvider>
  )
}

/** Exposes the cockpit's small notify/error API on top of the kit's toast queue. */
function Bridge({ children }: { children: ReactNode }) {
  const { addToast } = useKitToast()
  const api = useMemo<ToastApi>(() => {
    const notify = (message: string, kind: ToastKind = 'info') => addToast({ children: message, variation: variation[kind], className: styles.toast })
    return {
      notify,
      error: (error, prefix) => {
        const message = error instanceof Error ? error.message : String(error)
        notify(prefix ? `${prefix}: ${message}` : message, 'error')
      },
    }
  }, [addToast])
  return <ToastContext.Provider value={api}>{children}</ToastContext.Provider>
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext)
  if (!context) throw new Error('useToast must be used inside ToastProvider')
  return context
}
